import React from 'react';

import PropTypes from 'prop-types';

import clsx from 'clsx';

import {
    DisclaimerText,
    SideBarToModal,
    StarRating,
    SubHeading,
    Text,
} from 'reaxl';
import {
    CollapsibleListColumns,
} from 'reaxl-molecules';

/**
 *  The ModelInformation component renders a SideBar(sm and sx) or a Modal (md and lg) with model information on it
 *
 */
function ModelInformation({
    title,
    features = {},
    collapsed = true,
    show = true,
    onHide = () => {},
    customFeatures = {},
    customOrder = [],
    ...rest
}) {

    const valueClassName = (available) => clsx({
        'text-subdued-muted': !available,
        'text-right': true,
        'text-bold': available,
    });

    const renderValue = (key, available, value) => (
        key === 'Safety Ratings' && available ? <StarRating value={value} /> : <div className={valueClassName(available)}>{value}</div>
    );

    const orderOfKeys = customOrder.length > 0 ? customOrder : Object.keys(features).concat(Object.keys(customFeatures));

    const featuresAndSpecifications = orderOfKeys.map((key) => {
        let specSectionContent;
        if (features[key] && features[key].length > 0) {
            specSectionContent = (
                <CollapsibleListColumns
                    items={features[key]}
                    render={({ label, value }) => (
                        <div className="display-flex justify-content-between">
                            <div className="padding-right-2">{label}</div>
                            {renderValue(key, value !== 'Information Unavailable', value)}
                        </div>
                    )}
                    sm={2}
                    md={2}
                    lg={2}
                    bordered
                    condensed
                />
            );
        } else if (customFeatures[key]) {
            specSectionContent = customFeatures[key];
        } else return null;

        return (
            <div key={key}>
                <SubHeading size={300}>{key}</SubHeading>
                {specSectionContent}
            </div>
        );
    });
    const defaultModelInformation = (
        <div className="padding-horizontal-4">
            <Text
                componentClass="div"
                size={500}
                className="margin-bottom-2"
            >
                {title}
            </Text>

            <DisclaimerText className="margin-bottom-5">
                This is general model information, and some of the features and/or specifications mentioned may not be available on all vehicles.
            </DisclaimerText>

            {featuresAndSpecifications}
        </div>
    );

    return (
        show && (
            <div {...rest}>
                <SideBarToModal
                    title="Features and Specifications"
                    content={
                        defaultModelInformation
                    }
                    collapsed={collapsed}
                    show={show}
                    onHide={onHide}
                    sideBarOptions={{
                        fullWidth: true,
                    }}
                    modalOptions={{
                        bsSize: 'large',
                        scrollPanel: true,
                    }}
                />
            </div>
        )
    );
}

ModelInformation.propTypes = {
    /**
     * The features object contains features related to the model
     */
    features: PropTypes.object,
    /**
     * The title to be displayed on modal
     */
    title: PropTypes.string,
    /**
    * The className to apply to the root node
    */
    className: PropTypes.string,
    /**
     * If true, ModelInformation will render in SideBar mode
     */
    collapsed: PropTypes.bool,
    /**
     * If true, ModelInformation will show
     */
    show: PropTypes.bool,
    /**
     * Callback function fired when ModelInformation is hidden
     */
    onHide: PropTypes.func,
};

export default ModelInformation;
